import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Award, MapPin, Quote } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

const CATEGORIES = ["All", "Breakthrough", "Healing", "Salvation", "Provision", "Family", "Other"];

export default function Testimonies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [amenIds, setAmenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("amen_ids") || "[]")); }
    catch { return new Set(); }
  });

  const empty = {
    name: user?.name || "",
    email: user?.email || "",
    title: "",
    message: "",
    category: "Breakthrough",
    location: "",
    is_anonymous: false,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    const url = category === "All" ? "/testimonies" : `/testimonies?category=${encodeURIComponent(category)}`;
    api.get(url).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3 || form.message.trim().length < 20) {
      toast.error("Please give your story a title and share at least a couple sentences.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/testimonies", {
        name: form.is_anonymous ? "Anonymous" : form.name.trim(),
        email: form.email || undefined,
        title: form.title.trim(),
        message: form.message.trim(),
        category: form.category,
        location: form.location.trim() || undefined,
        is_anonymous: form.is_anonymous,
      });
      toast.success("Praise the Lord! Your story is on the wall.");
      setForm({ ...empty, name: form.name, email: form.email });
      if (form.category === category || category === "All") {
        setItems((prev) => [data.testimony, ...prev]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const amen = async (id) => {
    if (amenIds.has(id)) return;
    try {
      const { data } = await api.post(`/testimonies/${id}/amen`);
      setItems((prev) => prev.map((t) => t.id === id ? { ...t, amen_count: data.amen_count } : t));
      const next = new Set(amenIds);
      next.add(id);
      setAmenIds(next);
      localStorage.setItem("amen_ids", JSON.stringify([...next]));
      if (!data.already_amen) toast.success("Amen! 🙌");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const totalAmens = useMemo(() => items.reduce((n, t) => n + (t.amen_count || 0), 0), [items]);

  return (
    <div data-testid="testimonies-page">
      <PageHero
        overline="Stories of Faith"
        title={<>What the Lord <span className="font-accent text-gold-gradient italic">has done.</span></>}
        subtitle="Stories of breakthrough, healing, restoration, provision, and salvation. Read what God is doing across the nations. Share your own story and stir faith in someone tonight."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24">
        <div className="grid gap-12 lg:grid-cols-[420px_1fr]">
          {/* Submit form */}
          <Reveal>
            <div className="lg:sticky lg:top-28 border border-gold-500/25 bg-ink-paper/40 p-7" data-testid="testimony-form-card">
              <div className="flex items-center gap-3 mb-1">
                <Award size={20} className="text-gold-500" />
                <Overline>Share your story</Overline>
              </div>
              <p className="text-sm text-white/60 mt-3 mb-6">Your story of faith is a weapon of victory. Share what God has done and stir faith in others.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="testimony-form">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    required={!form.is_anonymous}
                    disabled={form.is_anonymous}
                    placeholder={form.is_anonymous ? "Anonymous" : "Your name"}
                    value={form.is_anonymous ? "" : form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="testimony-name"
                    className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                  />
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="testimony-category"
                    className="bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400"
                  >
                    {CATEGORIES.filter((c) => c !== "All").map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <Input
                  placeholder="City / country (optional)"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="testimony-location"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                />
                <Input
                  type="email"
                  placeholder="Email (optional)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="testimony-email"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                />
                <Input
                  required
                  placeholder="Title (e.g. 'God restored my marriage')"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="testimony-title"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
                />
                <Textarea
                  required
                  rows={6}
                  minLength={20}
                  maxLength={3000}
                  placeholder="Tell your story — what were you facing, what did God do, how has your life changed?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="testimony-message"
                  className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 resize-none"
                />
                <label className="flex items-center justify-between gap-3 cursor-pointer" data-testid="testimony-toggle-anonymous">
                  <span className="text-sm text-white/75">Share anonymously</span>
                  <Switch
                    checked={form.is_anonymous}
                    onCheckedChange={(v) => setForm({ ...form, is_anonymous: v })}
                  />
                </label>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="testimony-submit"
                  className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold"
                >
                  <Send size={15} className="mr-2" />
                  {submitting ? "Sharing…" : "Share my story"}
                </Button>
              </form>
            </div>
          </Reveal>

          {/* Feed */}
          <div>
            <Reveal className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <Overline>The story wall</Overline>
                <p className="mt-2 text-sm text-white/60" data-testid="testimonies-stats">
                  {items.length} {items.length === 1 ? "story" : "stories"} · {totalAmens} amen{totalAmens === 1 ? "" : "s"} declared
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="testimony-categories">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`tcat-${c.toLowerCase()}`}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                      category === c ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/15 text-white/60 hover:text-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>

            {loading ? (
              <p className="text-white/50 py-14 text-center border border-gold-500/15" data-testid="testimonies-loading">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-white/50 py-14 text-center border border-gold-500/15" data-testid="testimonies-empty">
                No stories here yet. Be the first to share what God has done.
              </p>
            ) : (
              <div className="grid gap-6 md:grid-cols-2" data-testid="testimonies-grid">
                <AnimatePresence>
                  {items.map((t) => {
                    const iSaid = amenIds.has(t.id);
                    return (
                      <motion.article
                        key={t.id}
                        layout
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col justify-between border border-gold-500/20 bg-ink-paper/40 p-7"
                        data-testid={`testimony-card-${t.id}`}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30 px-2.5 py-1">
                                {t.category}
                              </span>
                              {t.is_featured && t.role && (
                                <span className="text-[10px] uppercase tracking-[0.2em] text-ink bg-gold-500 px-2.5 py-1" data-testid={`featured-${t.id}`}>
                                  ★ {t.role}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-white/40">{timeAgo(t.created_at)}</span>
                          </div>
                          <h3 className="mt-5 font-serif text-2xl text-white leading-tight">{t.title}</h3>
                          <div className="mt-2 flex items-center gap-2 text-xs text-white/50">
                            <span className="text-gold-300">{t.name}</span>
                            {t.location && (
                              <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1"><MapPin size={11} />{t.location}</span>
                              </>
                            )}
                          </div>
                          <p className="mt-5 text-white/80 leading-relaxed text-[15px] whitespace-pre-wrap">
                            {t.message}
                          </p>
                        </div>
                        <div className="mt-6 pt-5 border-t border-gold-500/15 flex items-center justify-between gap-3">
                          <Quote size={16} className="text-gold-500/50" />
                          <button
                            onClick={() => amen(t.id)}
                            disabled={iSaid}
                            data-testid={`amen-btn-${t.id}`}
                            className={`inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                              iSaid
                                ? "border-gold-500/50 bg-gold-500/10 text-gold-300 cursor-default"
                                : "border-gold-500/30 text-white hover:bg-gold-500 hover:text-ink hover:border-gold-500"
                            }`}
                          >
                            <Sparkles size={13} />
                            <span>{iSaid ? "Amen given" : "Amen"}</span>
                            <span className="text-gold-400">· {t.amen_count || 0}</span>
                          </button>
                        </div>
                      </motion.article>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
